import React, { forwardRef } from 'react';
import { motion } from 'framer-motion';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Loader2, Trash2 } from 'lucide-react';

const CustomAnalysisCard = forwardRef(({ type, onDelete, deleteLoading }, ref) => {
    return (
        <motion.div
            ref={ref}
            layout
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{
                opacity: { duration: 0.2 },
                scale: { duration: 0.2 },
                layout: { duration: 0.3 }
            }}
        >
            <Card className="flex flex-col h-full">
                <CardHeader>
                    <div className="flex justify-between items-start">
                        <div>
                            <CardTitle className="mb-2">{type.name}</CardTitle>
                            <CardDescription>{type.description}</CardDescription>
                        </div>
                        <AlertDialog>
                            <AlertDialogTrigger asChild>
                                <Button variant="destructive" size="sm" className="flex items-center gap-2 ml-2">
                                    {deleteLoading === type.id ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                        <Trash2 className="w-4 h-4" />
                                    )}
                                </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                                <AlertDialogHeader>
                                    <AlertDialogTitle>Delete Custom Analysis Type</AlertDialogTitle>
                                    <AlertDialogDescription>
                                        Are you sure you want to delete "{type.name}"? This action cannot be undone.
                                    </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction className="bg-red-700 hover:bg-red-800" onClick={() => onDelete(type.id)}>Delete</AlertDialogAction>
                                </AlertDialogFooter>
                            </AlertDialogContent>
                        </AlertDialog>
                    </div>
                </CardHeader>
                <CardContent className="mt-auto space-y-2">
                    <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Created</span>
                        <span className="font-medium">
                            {new Date(type.created_at).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                            })}
                        </span>
                    </div>
                    <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Last used</span>
                        <span className="font-medium">
                            {type.last_used_at
                                ? new Date(type.last_used_at).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                })
                                : 'Never used'}
                        </span>
                    </div>
                </CardContent>
            </Card>
        </motion.div>
    );
});

// Add a display name for better debugging
CustomAnalysisCard.displayName = 'CustomAnalysisCard';

export default CustomAnalysisCard;